import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import SelectInput from "@components/inputs/SelectInput/SelectInput";
import { CHARITY_REGION_OPTIONS } from "@util/domain";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import styles from "@styles/CommonEditorPageLayout.module.css";
import { isRequiredField, isValidMaxLength, isValidUrl } from "@util/validators";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import { MAX_CHARITY_LOGO_SIZE, MAX_CHARITY_NAME_LENGTH } from "@constants/validation";
import { createCharity, fetchCharity, updateCharity } from "@services/charityService";
import { MediaFile } from "@models/Common";
import { CharityUpdate } from "@models/Charity";
import { delay } from "@util/time";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { AxiosProgressEvent } from "axios";
import CheckboxWithLabel from "@components/inputs/Checkbox/CheckboxWithLabel";

const CharityEditorPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");

    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [changedFields, setChangedFields] = useState<CharityUpdate>({});

    const [charityName, setCharityName] = useState("");
    const [logo, setLogo] = useState<MediaFile | null>(null);
    const [url, setUrl] = useState("");
    const [donationUrl, setDonationUrl] = useState("");
    const [isPetCharity, setIsPetCharity] = useState(false);
    const [region, setRegion] = useState<string>("au"); // New charities default to Australia

    const [charityNameError, setCharityNameError] = useState("");
    const [logoError, setLogoError] = useState("");
    const [urlError, setUrlError] = useState("");
    const [donationUrlError, setDonationUrlError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const hasUnsavedChanges = Object.keys(changedFields).length > 0;

    // Load Charity information in edit mode
    useEffect(() => {
        if (mode === "edit") {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    const data = await fetchCharity(id);
                    setCharityName(data.name);
                    setLogo(data.logo);
                    setUrl(data.url);
                    setDonationUrl(data.donationUrl);
                    setIsPetCharity(data.isPetCharity || false);
                    setRegion(data.region || "au");
                } catch (err) {
                    setError("Failed to fetch charity information.");
                } finally {
                    setLoading(false);
                }
            };
            void fetchData();
        }
    }, [mode, id]);

    // Validation functions
    const validateCharityName = useCallback((val: string) => {
        const result = isRequiredField(val, "Charity name");
        if (result) {
            return result;
        }
        return isValidMaxLength(val, MAX_CHARITY_NAME_LENGTH);
    }, []);

    const validateLink = useCallback((val: string) => {
        return isValidUrl(val);
    }, []);

    // Handlers with validation
    const onCharityNameChange = useCallback((val: string) => {
        setCharityName(val);
        setCharityNameError(validateCharityName(val));
        setChangedFields((prev) => ({ ...prev, name: val }));
    }, [validateCharityName]);

    const onUrlChange = useCallback((val: string) => {
        setUrl(val);
        setUrlError(validateLink(val));
        setChangedFields((prev) => ({ ...prev, url: val }));
    }, [validateLink]);

    const onDonationUrlChange = useCallback((val: string) => {
        setDonationUrl(val);
        setDonationUrlError(validateLink(val));
        setChangedFields((prev) => ({ ...prev, donationUrl: val }));
    }, [validateLink]);

    const progressCallback = useCallback((progressEvent: AxiosProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
    }, []);

    const handleSave = async () => {
        let isValid = true;

        // Validate Charity Name
        const charityNameValidationError = validateCharityName(charityName);
        setCharityNameError(charityNameValidationError);
        if (charityNameValidationError) isValid = false;

        if (logoError) isValid = false;

        // Validate Website Link
        const urlValidationError = validateLink(url);
        setUrlError(urlValidationError);
        if (urlValidationError) isValid = false;

        // Validate Donation Link
        const donationUrlValidationError = validateLink(donationUrl);
        setDonationUrlError(donationUrlValidationError);
        if (donationUrlValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                let createdId = "";

                if (mode === "add") {
                    createdId = await createCharity(changedFields, progressCallback);
                } else {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    await updateCharity(id, changedFields, progressCallback);
                }

                setChangedFields({});
                setIsSaving(false);
                await delay(1); // Wait for saving state to be cleared
                if (mode === "add") {
                    // Change to edit mode after creation
                    searchParams.set("mode", "edit");
                    searchParams.set("id", createdId);
                    navigate(`?${searchParams.toString()}`, { replace: true });
                }
            } catch (err) {
                const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to save charity information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={changedFields.logo ? uploadProgress : undefined}
                    error={error}
                />
            );
        }

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>
                        {mode === "add" ? "Add" : "Edit"}
                        {" "}
                        Charity
                    </h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        isNew={mode === "add"}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.contentContainer}>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="Charity Name *"
                            id="charityName"
                            value={charityName}
                            onChange={onCharityNameChange}
                            placeholder="Enter charity name..."
                            errorMessage={charityNameError}
                        />
                        <MediaSelector
                            label="Logo"
                            id="logo"
                            media={logo}
                            errorMessage={logoError}
                            onMediaChange={(img, err) => {
                                setLogo(img as MediaFile);
                                setLogoError(err);
                                setChangedFields((prev) => ({ ...prev, logo: img as MediaFile }));
                            }}
                            maxSize={MAX_CHARITY_LOGO_SIZE}
                            mediaTypeFilter="image"
                        />
                        <TextInput
                            label="Website Link *"
                            id="url"
                            value={url}
                            onChange={onUrlChange}
                            placeholder="Enter website link..."
                            errorMessage={urlError}
                        />
                        <TextInput
                            label="Donation Link *"
                            id="donationUrl"
                            value={donationUrl}
                            onChange={onDonationUrlChange}
                            placeholder="Enter donation link..."
                            errorMessage={donationUrlError}
                        />
                        <SelectInput
                            label="Offered in *"
                            id="region"
                            value={region}
                            onChange={(val) => {
                                setRegion(val);
                                setChangedFields((prev) => ({ ...prev, region: (val === "global" || val === "both") ? val : "au" }));
                            }}
                            options={CHARITY_REGION_OPTIONS}
                            placeholder="Select region..."
                        />
                        <CheckboxWithLabel
                            id="isPetCharity"
                            label="Pet Charity"
                            checked={isPetCharity}
                            onChange={(val) => {
                                setIsPetCharity(val);
                                setChangedFields((prev) => ({ ...prev, isPetCharity: val }));
                            }}
                        />
                        {submitErrorMessage && (
                            <p className={styles.errorMessage}>{submitErrorMessage}</p>
                        )}
                    </Card>
                </div>
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default CharityEditorPage;
